import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { supabase } from '../lib/supabase'
import AddCategoryInline from './AddCategoryInline'
import AddPaymentMethodInline from './AddPaymentMethodInline'

const teal = '#0D9488'
const plum = '#1E1040'

// Tag-type colors
const tagAccents = {
  schedule_c: teal,
  company_card: '#2563EB',
  reimbursable: '#F59E0B',
  personal: '#A855F7',
}

/**
 * ReceiptDetailModal
 * Props:
 *   receipt: the transaction row
 *   tags: array of tag rows
 *   categories: array of category rows
 *   paymentMethods: array of saved payment methods
 *   onClose, onUpdated(row), onDeleted(id)
 */
export default function ReceiptDetailModal({
  receipt,
  tags: propTags = [],
  categories: propCategories = [],
  paymentMethods: propPaymentMethods = [],
  startInEdit = false,
  highlightTag = false,
  onClose,
  onUpdated,
  onDeleted,
}) {
  const { t } = useTranslation()

  const tagHint = (tag) => {
    if (!tag) return ''
    return t(`receipt_modal.tag_hint.${tag.tag_type}`, { defaultValue: '' })
  }

  // Open straight into edit mode when launched to fix an untagged receipt
  const [editing, setEditing] = useState(startInEdit)
  const [merchant, setMerchant] = useState(receipt.merchant || '')
  const [amount, setAmount] = useState(String(receipt.amount || ''))
  const [date, setDate] = useState(receipt.transaction_date || '')
  const [categoryId, setCategoryId] = useState(receipt.category_id || '')
  const [paymentMethodId, setPaymentMethodId] = useState(receipt.payment_method_id || '')
  const [memo, setMemo] = useState(receipt.memo || '')
  const [tagId, setTagId] = useState(receipt.tag_id || (propTags[0]?.id || ''))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [categories, setCategories] = useState(propCategories || [])
  const [tags] = useState(propTags || [])
  const [paymentMethods, setPaymentMethods] = useState(propPaymentMethods || [])
  const [showAddCategory, setShowAddCategory] = useState(false)
  const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false)

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [onClose])

  const handleSave = async () => {
    if (!merchant || !amount) {
      setError(t('receipt_modal.error_required_fields'))
      return
    }
    setSaving(true)
    setError('')

    try {
      const selectedTag = tags.find(tt => tt.id === tagId)
      const { data: updated, error: err } = await supabase
        .from('transactions')
        .update({
          merchant,
          amount: parseFloat(amount),
          transaction_date: date,
          category_id: categoryId || null,
          payment_method_id: paymentMethodId || null,
          tag_id: tagId || null,
          memo: memo || null,
          is_business: selectedTag?.tag_type !== 'personal',
          updated_at: new Date().toISOString(),
        })
        .eq('id', receipt.id)
        .select()
        .single()

      if (err) throw err
      onUpdated?.(updated)
      setEditing(false)
    } catch (err) {
      setError(err.message || t('receipt_detail.error_save'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setSaving(true)
    setError('')
    try {
      if (receipt.receipt_file_path) {
        await supabase.storage.from('receipts').remove([receipt.receipt_file_path])
      }
      const { error: err } = await supabase
        .from('transactions')
        .delete()
        .eq('id', receipt.id)

      if (err) throw err
      onDeleted?.(receipt.id)
      onClose()
    } catch (err) {
      setError(err.message || t('receipt_detail.error_delete'))
      setSaving(false)
    }
  }

  const formatDate = (d) => {
    const lang = i18n.language || 'en'
    const locale = lang.startsWith('es') ? 'es-US' : 'en-US'
    return new Date(d + 'T00:00:00').toLocaleDateString(locale, {
      month: 'long', day: 'numeric', year: 'numeric',
    })
  }
  const formatCurrency = (n) => {
    const lang = i18n.language || 'en'
    const locale = lang.startsWith('es') ? 'es-US' : 'en-US'
    return new Intl.NumberFormat(locale, {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(parseFloat(n || 0))
  }

  const category = categories.find(c => c.id === receipt.category_id)
  const hasFile = !!receipt.receipt_url

  return (
    <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.title}>{editing ? t('receipt_detail.edit_title') : t('receipt_detail.title')}</div>
          <button style={styles.closeBtn} onClick={onClose} aria-label={t('common.close')}>✕</button>
        </div>

        {hasFile && receipt.receipt_file_type === 'image' && (
          <div style={styles.filePreview}>
            <img src={receipt.receipt_url} alt={t('receipt_modal.preview_alt')} style={styles.previewImg} />
          </div>
        )}
        {hasFile && receipt.receipt_file_type === 'pdf' && (
          <a href={receipt.receipt_url} target="_blank" rel="noopener noreferrer" style={styles.pdfLink}>
            <div style={styles.pdfIcon}>📄</div>
            <div>
              <div style={styles.pdfTitle}>{t('receipt_detail.view_pdf')}</div>
              <div style={styles.pdfSub}>{t('receipt_detail.opens_new_tab')}</div>
            </div>
            <div style={styles.pdfArrow}>↗</div>
          </a>
        )}
        {!hasFile && (
          <div style={styles.noFile}>
            <div style={styles.noFileIcon}>📝</div>
            <div style={styles.noFileText}>{t('receipt_detail.no_file')}</div>
          </div>
        )}

        {!editing && (
          <div style={styles.viewBlock}>
            <div style={styles.viewAmount}>{formatCurrency(receipt.amount)}</div>
            <div style={styles.viewMerchant}>{receipt.merchant || t('receipt_detail.unknown_merchant')}</div>
            <div style={styles.viewDate}>
              {formatDate(receipt.transaction_date)}
              {(() => {
                const receiptTag = tags.find(tt => tt.id === receipt.tag_id)
                if (!receiptTag) return null
                const accent = tagAccents[receiptTag.tag_type] || teal
                return (
                  <span style={{
                    ...styles.typeBadgeBase,
                    color: accent,
                    background: `${accent}1A`,
                  }}>
                    {receiptTag.icon} {receiptTag.name}
                  </span>
                )
              })()}
            </div>

            <div style={styles.metaGrid}>
              {category && (
                <div style={styles.metaRow}>
                  <span style={styles.metaLabel}>{t('receipt_modal.category_label')}</span>
                  <span style={styles.metaValue}>{category.icon} {category.name}</span>
                </div>
              )}
              {(() => {
                const pm = paymentMethods.find(p => p.id === receipt.payment_method_id)
                if (pm) {
                  return (
                    <div style={styles.metaRow}>
                      <span style={styles.metaLabel}>{t('receipt_modal.payment_label')}</span>
                      <span style={styles.metaValue}>{pm.label}{pm.last_4 ? ` ••${pm.last_4}` : ''}</span>
                    </div>
                  )
                }
                if (receipt.payment_method) {
                  return (
                    <div style={styles.metaRow}>
                      <span style={styles.metaLabel}>{t('receipt_modal.payment_label')}</span>
                      <span style={styles.metaValue}>{receipt.payment_method}</span>
                    </div>
                  )
                }
                return null
              })()}
              {receipt.memo && (
                <div style={styles.metaRow}>
                  <span style={styles.metaLabel}>{t('receipt_detail.memo_label')}</span>
                  <span style={styles.metaValue}>{receipt.memo}</span>
                </div>
              )}
              {receipt.ai_extracted && (
                <div style={styles.metaRow}>
                  <span style={styles.metaLabel}>{t('receipt_detail.source_label')}</span>
                  <span style={{ ...styles.metaValue, color: teal }}>✨ {t('receipt_detail.ai_extracted')}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {editing && (
          <div style={styles.form}>
            {tags.length > 0 && (
              <div style={styles.field}>
                <label style={styles.label}>{t('receipt_modal.tag_label')}</label>
                <select
                  style={highlightTag ? { ...styles.input, ...styles.inputHighlight } : styles.input}
                  value={tagId}
                  autoFocus={highlightTag}
                  onChange={e => { setTagId(e.target.value); setCategoryId(''); setShowAddCategory(false) }}
                >
                  {tags.map(tag => (
                    <option key={tag.id} value={tag.id}>{tag.icon} {tag.name}</option>
                  ))}
                </select>
                <div style={styles.hint}>
                  {tagHint(tags.find(tt => tt.id === tagId))}
                </div>
              </div>
            )}

            <div style={styles.field}>
              <label style={styles.label}>{t('receipt_modal.merchant_label')}</label>
              <input type="text" style={styles.input} value={merchant} onChange={e => setMerchant(e.target.value)} />
            </div>
            <div style={styles.fieldRow}>
              <div style={styles.field}>
                <label style={styles.label}>{t('receipt_modal.amount_label')}</label>
                <input type="number" step="0.01" style={styles.input} value={amount} onChange={e => setAmount(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>{t('receipt_modal.date_label')}</label>
                <input type="date" style={styles.input} value={date} onChange={e => setDate(e.target.value)} />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{t('receipt_modal.category_label')}</label>
              <select
                style={styles.input}
                value={categoryId}
                onChange={e => {
                  if (e.target.value === '__ADD_NEW__') {
                    setShowAddCategory(true)
                  } else {
                    setCategoryId(e.target.value)
                  }
                }}
              >
                <option value="">{t('receipt_modal.select_placeholder')}</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                <option value="__ADD_NEW__">➕ {t('receipt_modal.add_category_option')}</option>
              </select>
              {showAddCategory && (
                <AddCategoryInline
                  categoryType={(() => {
                    const tag = tags.find(tt => tt.id === tagId)
                    return tag?.tag_type === 'personal' ? 'personal' : 'business'
                  })()}
                  variant={(() => {
                    const tag = tags.find(tt => tt.id === tagId)
                    return tag?.tag_type === 'personal' ? 'personal' : 'business'
                  })()}
                  onAdded={(newCat) => {
                    setCategories(prev => [...prev, newCat])
                    setCategoryId(newCat.id)
                    setShowAddCategory(false)
                  }}
                  onCancel={() => setShowAddCategory(false)}
                />
              )}
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{t('receipt_modal.payment_label')}</label>
              <select
                style={styles.input}
                value={paymentMethodId}
                onChange={e => {
                  if (e.target.value === '__ADD_NEW_PM__') {
                    setShowAddPaymentMethod(true)
                  } else {
                    setPaymentMethodId(e.target.value)
                  }
                }}
              >
                <option value="">{t('receipt_modal.select_payment_placeholder')}</option>
                {paymentMethods.map(pm => (
                  <option key={pm.id} value={pm.id}>
                    {pm.label}{pm.last_4 ? ` ••${pm.last_4}` : ''}
                  </option>
                ))}
                <option value="__ADD_NEW_PM__">➕ {t('receipt_modal.add_payment_option')}</option>
              </select>
              {showAddPaymentMethod && (
                <AddPaymentMethodInline
                  defaultTagId={tagId}
                  onAdded={(newPm) => {
                    setPaymentMethods(prev => [...prev, newPm])
                    setPaymentMethodId(newPm.id)
                    setShowAddPaymentMethod(false)
                  }}
                  onCancel={() => setShowAddPaymentMethod(false)}
                />
              )}
              {paymentMethods.length === 0 && !showAddPaymentMethod && (
                <div style={styles.hint}>
                  {t('receipt_modal.no_payment_hint')}
                </div>
              )}
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{t('receipt_detail.memo_label')}</label>
              <input type="text" style={styles.input} value={memo} onChange={e => setMemo(e.target.value)} />
            </div>
          </div>
        )}

        {error && <div style={styles.error}>{error}</div>}

        {!editing && !confirmDelete && (
          <div style={styles.actions}>
            <button style={styles.btnDanger} onClick={() => setConfirmDelete(true)}>{t('common.delete')}</button>
            <button style={styles.btnPrimary} onClick={() => setEditing(true)}>{t('common.edit')}</button>
          </div>
        )}

        {editing && (
          <div style={styles.actions}>
            <button style={styles.btnSecondary} onClick={() => setEditing(false)}>{t('common.cancel')}</button>
            <button
              style={{ ...styles.btnPrimary, opacity: saving ? 0.5 : 1 }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? t('common.saving') : t('receipt_detail.save_changes')}
            </button>
          </div>
        )}

        {confirmDelete && (
          <div style={styles.confirmBlock}>
            <div style={styles.confirmText}>{t('receipt_detail.delete_confirm')}</div>
            <div style={styles.actions}>
              <button style={styles.btnSecondary} onClick={() => setConfirmDelete(false)}>{t('common.cancel')}</button>
              <button style={styles.btnDangerSolid} onClick={handleDelete} disabled={saving}>
                {saving ? t('receipt_detail.deleting') : t('receipt_detail.confirm_delete')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 2000,
    background: 'rgba(15, 23, 42, 0.75)',
    backdropFilter: 'blur(6px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
  },
  modal: {
    background: '#fff', borderRadius: '20px', padding: '24px',
    width: '100%', maxWidth: '520px', maxHeight: '92vh',
    overflowY: 'auto', boxShadow: '0 32px 80px rgba(0,0,0,0.3)',
    fontFamily: "'Plus Jakarta Sans', sans-serif", color: plum,
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  title: { fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: 800, color: plum, letterSpacing: '-0.3px' },
  closeBtn: {
    background: '#F3F4F6', border: 'none', borderRadius: '50%',
    width: '36px', height: '36px',
    fontSize: '14px', color: '#6B7280', cursor: 'pointer', fontWeight: 700,
  },
  filePreview: { borderRadius: '12px', overflow: 'hidden', background: '#F9FAFB', border: '1px solid #E5E7EB', marginBottom: '20px' },
  previewImg: { display: 'block', width: '100%', maxHeight: '300px', objectFit: 'contain' },
  pdfLink: {
    display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '14px',
    alignItems: 'center', padding: '16px 20px',
    background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '12px',
    marginBottom: '20px', textDecoration: 'none', color: plum,
  },
  pdfIcon: { fontSize: '32px' },
  pdfTitle: { fontSize: '14px', fontWeight: 700, marginBottom: '2px' },
  pdfSub: { fontSize: '12px', color: '#6B7280' },
  pdfArrow: { fontSize: '18px', color: teal },
  noFile: {
    display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '12px',
    alignItems: 'center', padding: '14px 18px',
    background: '#F9FAFB', border: '1px dashed #E5E7EB', borderRadius: '12px',
    marginBottom: '20px',
  },
  noFileIcon: { fontSize: '24px' },
  noFileText: { fontSize: '13px', color: '#6B7280', fontWeight: 500 },
  viewBlock: { marginBottom: '16px' },
  viewAmount: {
    fontFamily: "'Fraunces', serif", fontSize: '38px', fontWeight: 900,
    color: plum, letterSpacing: '-1px', lineHeight: 1, marginBottom: '6px',
  },
  viewMerchant: { fontSize: '17px', fontWeight: 700, color: plum, marginBottom: '2px' },
  viewDate: {
    fontSize: '14px', color: '#6B7280', marginBottom: '20px', fontWeight: 500,
    display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
  },
  typeBadgeBase: {
    fontSize: '11px', fontWeight: 700,
    padding: '3px 10px', borderRadius: '100px', letterSpacing: '0.02em',
    display: 'inline-flex', alignItems: 'center', gap: '4px',
  },
  hint: { fontSize: '12px', color: '#9CA3AF', marginTop: '6px', fontWeight: 500, lineHeight: 1.4 },
  metaGrid: { background: '#F9FAFB', borderRadius: '12px', padding: '14px 18px' },
  metaRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '7px 0', borderBottom: '1px solid #EEF2F7', fontSize: '14px',
  },
  metaLabel: { color: '#6B7280', fontWeight: 500 },
  metaValue: { color: plum, fontWeight: 600, textAlign: 'right' },
  form: { marginTop: '4px' },
  field: { marginBottom: '14px' },
  fieldRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  label: {
    display: 'block', fontSize: '12px', fontWeight: 700, color: '#6B7280',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px',
  },
  input: {
    width: '100%', padding: '11px 14px', borderRadius: '10px',
    border: '1.5px solid #E5E7EB', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box', color: plum,
    fontFamily: "'Plus Jakarta Sans', sans-serif", background: '#fff',
  },
  inputHighlight: {
    borderColor: '#B45309',
    boxShadow: '0 0 0 3px rgba(180, 83, 9, 0.15)',
  },
  error: {
    background: '#FEE2E2', color: '#991B1B',
    padding: '10px 14px', borderRadius: '8px',
    fontSize: '13px', marginTop: '12px', fontWeight: 500,
  },
  actions: { display: 'flex', gap: '10px', marginTop: '20px' },
  btnPrimary: {
    flex: 2, padding: '13px', background: teal, color: '#042f2e',
    border: 'none', borderRadius: '100px', fontSize: '14px', fontWeight: 700,
    cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 4px 14px ${teal}40`,
  },
  btnSecondary: {
    flex: 1, padding: '13px', background: '#fff', color: plum,
    border: '1.5px solid #E5E7EB', borderRadius: '100px',
    fontSize: '14px', fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  btnDanger: {
    flex: 1, padding: '13px', background: '#fff', color: '#DC2626',
    border: '1.5px solid #FECACA', borderRadius: '100px',
    fontSize: '14px', fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  btnDangerSolid: {
    flex: 2, padding: '13px', background: '#DC2626', color: '#fff',
    border: 'none', borderRadius: '100px',
    fontSize: '14px', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  confirmBlock: { marginTop: '16px' },
  confirmText: {
    background: '#FEF3C7', color: '#92400E',
    padding: '12px 16px', borderRadius: '10px',
    fontSize: '13px', fontWeight: 500,
  },
}
